import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge, { STATUSES } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Copy, Trash2, GripVertical, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Experiments() {
  const { user } = useAuth();
  const isSupervisor = user?.account_role === "supervisor";
  const [params, setParams] = useSearchParams();
  const [experiments, setExperiments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [projectId, setProjectId] = useState(params.get("project_id") || "all");
  const [scope, setScope] = useState(params.get("scope") || "mine");
  const [view, setView] = useState("list");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const query = {};
    if (q) query.q = q;
    if (status !== "all") query.status = status;
    if (projectId !== "all") query.project_id = projectId;
    if (isSupervisor) query.scope = scope;
    const { data } = await api.get("/experiments", { params: query });
    setExperiments(data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  useEffect(() => {
    load();
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (status !== "all") next.set("status", status);
    if (projectId !== "all") next.set("project_id", projectId);
    if (isSupervisor && scope !== "mine") next.set("scope", scope);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, projectId, scope]);

  const onDuplicate = async (id) => {
    try {
      await api.post(`/experiments/${id}/duplicate`);
      toast.success("Experiment duplicated");
      load();
    } catch (e) { toast.error("Failed to duplicate"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this experiment? This cannot be undone.")) return;
    try {
      await api.delete(`/experiments/${id}`);
      toast.success("Experiment deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const grouped = useMemo(() => {
    const g = { Planned: [], "In Progress": [], Completed: [], Failed: [] };
    for (const e of experiments) (g[e.status] || g.Planned).push(e);
    return g;
  }, [experiments]);

  const activeExperiments = experiments.filter((e) => !e.is_template);
  const templates = experiments.filter((e) => e.is_template);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-6 animate-fade-in" data-testid="experiments-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">Experiments</div>
          <h1 className="text-3xl sm:text-4xl font-sans font-bold tracking-tight mt-1">All experiments</h1>
        </div>
        <Link to="/experiments/new">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-experiment-btn">
            <Plus className="h-4 w-4 mr-2" /> New experiment
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-4 border border-zinc-200 dark:border-zinc-800 rounded-md bg-white dark:bg-zinc-900">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, objective, tag…"
            className="pl-9"
            data-testid="experiments-search-input"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px]" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-[200px]" data-testid="filter-project">
            <SelectValue placeholder="Project" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="list" data-testid="view-list">List</TabsTrigger>
            <TabsTrigger value="kanban" data-testid="view-kanban">Kanban</TabsTrigger>
          </TabsList>
        </Tabs>
        {isSupervisor && (
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList>
              <TabsTrigger value="mine" data-testid="scope-mine">Mine</TabsTrigger>
              <TabsTrigger value="all" data-testid="scope-all"><Users className="h-3.5 w-3.5 mr-1" />All members</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-sm font-mono text-zinc-500">loading experiments…</div>
      ) : view === "list" ? (
        <ListView items={activeExperiments} onDuplicate={onDuplicate} onDelete={onDelete} templates={templates} />
      ) : (
        <KanbanView grouped={grouped} />
      )}
    </div>
  );
}

function ListView({ items, onDuplicate, onDelete, templates }) {
  if (!items.length && !templates.length) {
    return (
      <div className="border border-dashed border-zinc-300 dark:border-zinc-700 rounded-md p-16 text-center" data-testid="experiments-empty">
        <div className="font-sans font-semibold text-lg">No experiments yet</div>
        <div className="text-sm font-body text-zinc-500 mt-1">Create your first experiment to start tracking.</div>
        <Link to="/experiments/new" className="inline-block mt-4">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="h-4 w-4 mr-2" /> New experiment</Button>
        </Link>
      </div>
    );
  }
  return (
    <>
      <div className="border border-zinc-200 dark:border-zinc-800 rounded-md overflow-hidden bg-white dark:bg-zinc-900">
        <table className="w-full text-sm font-body">
          <thead>
            <tr className="bg-zinc-50 dark:bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <th className="py-3 px-4 text-left">Title</th>
              <th className="py-3 px-4 text-left">Status</th>
              <th className="py-3 px-4 text-left">Start</th>
              <th className="py-3 px-4 text-left">End</th>
              <th className="py-3 px-4 text-left">Updated</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-zinc-100 dark:border-zinc-800/50 hover:bg-zinc-50/60 dark:hover:bg-zinc-800/40 transition-colors" data-testid={`exp-row-${e.id}`}>
                <td className="py-2.5 px-4">
                  <Link to={`/experiments/${e.id}`} className="font-medium hover:text-blue-600">{e.title}</Link>
                  {e.owner_email && (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-blue-600 mt-0.5">
                      by {e.owner_name || e.owner_email}
                    </div>
                  )}
                  {e.tags?.length ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {e.tags.slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] font-mono text-zinc-500 bg-zinc-100 dark:bg-zinc-800 rounded px-1.5">#{t}</span>
                      ))}
                    </div>
                  ) : null}
                </td>
                <td className="py-2.5 px-4"><StatusBadge status={e.status} /></td>
                <td className="py-2.5 px-4 text-xs font-mono text-zinc-500">{e.start_date || "—"}</td>
                <td className="py-2.5 px-4 text-xs font-mono text-zinc-500">{e.end_date || "—"}</td>
                <td className="py-2.5 px-4 text-xs font-mono text-zinc-500">{new Date(e.updated_at).toLocaleDateString()}</td>
                <td className="py-2.5 px-4 text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => onDuplicate(e.id)} title="Duplicate" data-testid={`duplicate-${e.id}`}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onDelete(e.id)} title="Delete" className="hover:text-red-600" data-testid={`delete-${e.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {templates.length > 0 && (
        <div className="mt-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">Templates</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map((t) => (
              <div key={t.id} className="border border-zinc-200 dark:border-zinc-800 rounded-md bg-white dark:bg-zinc-900 p-4" data-testid={`template-card-${t.id}`}>
                <div className="font-sans font-semibold">{t.title}</div>
                <div className="text-xs font-mono text-zinc-500 mt-1 truncate">{t.objective || "No objective"}</div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => onDuplicate(t.id)}>Use template</Button>
                  <Link to={`/experiments/${t.id}`}>
                    <Button size="sm" variant="ghost">Edit</Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function KanbanView({ grouped }) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
      {STATUSES.map((s) => (
        <div key={s} className="kanban-col min-w-[280px] w-[320px] flex flex-col max-h-[75vh]">
          <div className="flex items-center justify-between p-3 border-b border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-2">
              <GripVertical className="h-3.5 w-3.5 text-zinc-400" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{s}</span>
            </div>
            <span className="text-xs font-mono text-zinc-500">{grouped[s].length}</span>
          </div>
          <div className="p-3 space-y-2 overflow-y-auto">
            {grouped[s].length === 0 && (
              <div className="text-xs font-mono text-zinc-400 text-center py-8">empty</div>
            )}
            {grouped[s].map((e) => (
              <Link
                key={e.id}
                to={`/experiments/${e.id}`}
                className="block bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-md p-3 hover:border-blue-400 dark:hover:border-blue-600 transition-colors"
                data-testid={`kanban-card-${e.id}`}
              >
                <div className="font-body font-medium text-sm">{e.title}</div>
                {e.objective && (
                  <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{e.objective}</div>
                )}
                <div className="flex items-center justify-between mt-3 text-[10px] font-mono text-zinc-400">
                  <span>{e.start_date || "—"}</span>
                  <span>{new Date(e.updated_at).toLocaleDateString()}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
